import React, { useState, useEffect } from 'react';
import { Button } from '../shared/Button';
import { SpotifyPlayer, SpotifyPlayButton } from './SpotifyPlayer';
import { useSpotify } from '../../context/SpotifyContext';
import './SpotifySearch.css';
import './SpotifyPlayer.css';

interface SpotifyTrack {
  id: string;
  title: string;
  artist: string;
  album: string;
  year: number | null;
  duration: number;
  thumbnail: string | null;
  previewUrl: string | null;
  spotifyUrl: string;
  popularity?: number;
}

interface YouTubeMatch {
  id: string;
  title: string;
  channel: string;
  duration: number;
  thumbnail: string;
  url: string;
}

interface SpotifySearchProps {
  onAddSong: (song: {
    title: string;
    artist: string;
    year: number;
    youtubeUrl?: string;
    imageUrl?: string;
    previewUrl?: string;
    spotifyUri?: string;
  }) => void;
}

const API_BASE = process.env.REACT_APP_API_URL || '';

// Decades for quick filtering
const DECADES = [
  { label: '60s', start: 1960, end: 1969 },
  { label: '70s', start: 1970, end: 1979 },
  { label: '80s', start: 1980, end: 1989 },
  { label: '90s', start: 1990, end: 1999 },
  { label: '00s', start: 2000, end: 2009 },
  { label: '10s', start: 2010, end: 2019 },
  { label: '20s', start: 2020, end: 2029 },
];

export const SpotifySearch: React.FC<SpotifySearchProps> = ({ onAddSong }) => {
  const [isConfigured, setIsConfigured] = useState<boolean | null>(null);
  
  // Simple search
  const [query, setQuery] = useState('');
  
  // Advanced search fields
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [artistFilter, setArtistFilter] = useState('');
  const [trackFilter, setTrackFilter] = useState('');
  const [albumFilter, setAlbumFilter] = useState('');
  const [yearFrom, setYearFrom] = useState('');
  const [yearTo, setYearTo] = useState('');
  const [selectedDecade, setSelectedDecade] = useState<string | null>(null);
  const [onlyWithPreview, setOnlyWithPreview] = useState(false);
  
  // Results and state
  const [results, setResults] = useState<SpotifyTrack[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchLimit, setSearchLimit] = useState(10);
  
  // Configuration modal
  const [showConfig, setShowConfig] = useState(false);
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  
  // YouTube matching
  const [matchingTrack, setMatchingTrack] = useState<SpotifyTrack | null>(null);
  const [youtubeMatches, setYoutubeMatches] = useState<YouTubeMatch[]>([]);
  const [loadingMatches, setLoadingMatches] = useState(false);

  // Track which songs have been added to playlist (to prevent duplicates)
  const [addedTrackIds, setAddedTrackIds] = useState<Set<string>>(new Set());
  
  // Multi-select for batch adding
  const [selectedTracks, setSelectedTracks] = useState<Set<string>>(new Set());

  // Spotify SDK context
  const { isAuthenticated, isPremium, isReady } = useSpotify();

  // Check if Spotify is configured
  useEffect(() => {
    checkSpotifyStatus();
  }, []);

  const checkSpotifyStatus = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/spotify/status`);
      const data = await response.json();
      setIsConfigured(data.configured);
    } catch (err) {
      setIsConfigured(false);
    }
  };

  const handleConfigure = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/spotify/configure`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId, clientSecret }),
      });
      
      if (response.ok) {
        setIsConfigured(true);
        setShowConfig(false);
        setError(null);
      } else {
        setError('Configuratie mislukt');
      }
    } catch (err) {
      setError('Kon niet verbinden met server');
    }
  };

  // Build Spotify search query with filters
  const buildSearchQuery = (): string => {
    if (!showAdvanced) {
      return query.trim();
    }
    
    const parts: string[] = [];
    let hasSearchTerm = false;
    
    if (artistFilter.trim()) {
      parts.push(`artist:${artistFilter.trim()}`);
      hasSearchTerm = true;
    }
    if (trackFilter.trim()) {
      parts.push(`track:${trackFilter.trim()}`);
      hasSearchTerm = true;
    }
    if (albumFilter.trim()) {
      parts.push(`album:${albumFilter.trim()}`);
      hasSearchTerm = true;
    }
    if (yearFrom && yearTo) {
      parts.push(`year:${yearFrom}-${yearTo}`);
    } else if (yearFrom) {
      parts.push(`year:${yearFrom}-${new Date().getFullYear()}`);
    } else if (yearTo) {
      parts.push(`year:1900-${yearTo}`);
    }
    
    // Add any additional query terms
    if (query.trim()) {
      parts.push(query.trim());
      hasSearchTerm = true;
    }
    
    // Spotify needs actual search terms, not just year filter
    if (!hasSearchTerm) {
      return '';
    }
    
    return parts.join(' ');
  };

  const handleDecadeClick = (decade: typeof DECADES[0]) => {
    if (selectedDecade === decade.label) {
      setSelectedDecade(null);
      setYearFrom('');
      setYearTo('');
    } else {
      setSelectedDecade(decade.label);
      setYearFrom(decade.start.toString());
      setYearTo(decade.end.toString());
      setShowAdvanced(true);
    }
  };

  const clearFilters = () => {
    setQuery('');
    setArtistFilter('');
    setTrackFilter('');
    setAlbumFilter('');
    setYearFrom('');
    setYearTo('');
    setSelectedDecade(null);
    setOnlyWithPreview(false);
    setResults([]);
    setSelectedTracks(new Set());
  };

  const handleSearch = async () => {
    const searchQuery = buildSearchQuery();
    if (!searchQuery) {
      setError('Vul een artiest, nummer, album of zoekterm in. Alleen decennium selecteren is niet genoeg.');
      return;
    }

    setLoading(true);
    setError(null);
    setResults([]);
    setSelectedTracks(new Set());

    try {
      const response = await fetch(
        `${API_BASE}/api/spotify/search?query=${encodeURIComponent(searchQuery)}&limit=${searchLimit}`
      );
      const data = await response.json();

      if (data.error) {
        setError(data.message || data.error);
      } else {
        let filteredResults = data.results || [];
        
        // Client-side filter for preview availability
        if (onlyWithPreview) {
          filteredResults = filteredResults.filter((t: SpotifyTrack) => t.previewUrl);
        }
        
        setResults(filteredResults);
        
        if (filteredResults.length === 0) {
          setError('Geen resultaten gevonden. Probeer andere zoektermen.');
        }
      }
    } catch (err) {
      setError('Zoeken mislukt. Is de server actief?');
    } finally {
      setLoading(false);
    }
  };

  const toggleTrackSelection = (trackId: string) => {
    setSelectedTracks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(trackId)) {
        newSet.delete(trackId);
      } else {
        newSet.add(trackId);
      }
      return newSet;
    });
  };

  const handleAddSelectedTracks = () => {
    const tracksToAdd = results.filter(t => selectedTracks.has(t.id) && !addedTrackIds.has(t.id));
    
    tracksToAdd.forEach(track => {
      if (track.previewUrl) {
        handleAddWithPreview(track);
      } else if (isAuthenticated && isPremium) {
        handleAddWithSpotify(track);
      }
    });
    
    setSelectedTracks(new Set());
  };

  const selectAllWithPreview = () => {
    const previewTracks = results.filter(t => t.previewUrl && !addedTrackIds.has(t.id));
    setSelectedTracks(new Set(previewTracks.map(t => t.id)));
  };

  const handleFindYouTube = async (track: SpotifyTrack) => {
    setMatchingTrack(track);
    setLoadingMatches(true);
    setYoutubeMatches([]);

    try {
      const response = await fetch(
        `${API_BASE}/api/spotify/youtube-match?artist=${encodeURIComponent(track.artist)}&title=${encodeURIComponent(track.title)}&year=${track.year || ''}`
      );
      const data = await response.json();
      setYoutubeMatches(data.results || []);
    } catch (err) {
      setError('YouTube zoeken mislukt');
    } finally {
      setLoadingMatches(false);
    }
  };

  const handleAddWithYouTube = (track: SpotifyTrack, youtubeMatch: YouTubeMatch) => {
    if (addedTrackIds.has(track.id)) return;
    
    onAddSong({
      title: track.title,
      artist: track.artist,
      year: track.year || new Date().getFullYear(),
      youtubeUrl: youtubeMatch.url,
      imageUrl: track.thumbnail || youtubeMatch.thumbnail,
    });
    setAddedTrackIds(prev => new Set(prev).add(track.id));
    setMatchingTrack(null);
    setYoutubeMatches([]);
  };

  const handleAddWithPreview = (track: SpotifyTrack) => {
    if (addedTrackIds.has(track.id)) return;
    
    if (!track.previewUrl) {
      setError('Dit nummer heeft geen preview beschikbaar');
      return;
    }
    
    onAddSong({
      title: track.title,
      artist: track.artist,
      year: track.year || new Date().getFullYear(),
      previewUrl: track.previewUrl,
      imageUrl: track.thumbnail || undefined,
    });
    setAddedTrackIds(prev => new Set(prev).add(track.id));
  };

  const handleAddWithSpotify = (track: SpotifyTrack) => {
    if (addedTrackIds.has(track.id)) return;
    
    // Create Spotify URI from track ID
    const spotifyUri = `spotify:track:${track.id}`;
    
    onAddSong({
      title: track.title,
      artist: track.artist,
      year: track.year || new Date().getFullYear(),
      spotifyUri: spotifyUri,
      imageUrl: track.thumbnail || undefined,
    });
    setAddedTrackIds(prev => new Set(prev).add(track.id));
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Show configuration needed message
  if (isConfigured === false && !showConfig) {
    return (
      <div className="spotify-search">
        <div className="spotify-config-needed">
          <h3>🎧 Spotify Configuratie</h3>
          <p>Om Spotify te gebruiken heb je een Spotify Developer account nodig.</p>
          <ol>
            <li>Ga naar <a href="https://developer.spotify.com/dashboard" target="_blank" rel="noopener noreferrer">developer.spotify.com/dashboard</a></li>
            <li>Log in met je Spotify account</li>
            <li>Maak een nieuwe app aan</li>
            <li>Kopieer de Client ID en Client Secret</li>
          </ol>
          <Button variant="primary" onClick={() => setShowConfig(true)}>
            🔧 Spotify Configureren
          </Button>
        </div>
      </div>
    );
  }

  // Show configuration modal
  if (showConfig) {
    return (
      <div className="spotify-search">
        <div className="spotify-config-modal">
          <h3>🔧 Spotify API Configureren</h3>
          <div className="config-field">
            <label>Client ID:</label>
            <input
              type="text"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              placeholder="Jouw Spotify Client ID"
            />
          </div>
          <div className="config-field">
            <label>Client Secret:</label>
            <input
              type="password"
              value={clientSecret}
              onChange={(e) => setClientSecret(e.target.value)}
              placeholder="Jouw Spotify Client Secret"
            />
          </div>
          {error && <p className="error-message">{error}</p>}
          <div className="config-actions">
            <Button variant="secondary" onClick={() => setShowConfig(false)}>
              Annuleren
            </Button>
            <Button 
              variant="success" 
              onClick={handleConfigure}
              disabled={!clientId || !clientSecret}
            >
              ✓ Opslaan
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // YouTube matching modal
  if (matchingTrack) {
    return (
      <div className="spotify-search">
        <div className="youtube-match-modal">
          <h3>🎬 YouTube Video Kiezen</h3>
          <div className="matching-track-info">
            <img src={matchingTrack.thumbnail || ''} alt={matchingTrack.title} />
            <div>
              <strong>{matchingTrack.title}</strong>
              <span>{matchingTrack.artist} ({matchingTrack.year})</span>
            </div>
          </div>

          {loadingMatches ? (
            <div className="loading">Zoeken op YouTube...</div>
          ) : (
            <div className="youtube-matches">
              {youtubeMatches.map((match) => (
                <div key={match.id} className="youtube-match-item">
                  <img src={match.thumbnail} alt={match.title} />
                  <div className="match-info">
                    <div className="match-title">{match.title}</div>
                    <div className="match-channel">{match.channel}</div>
                  </div>
                  <Button
                    variant="success"
                    size="small"
                    onClick={() => handleAddWithYouTube(matchingTrack, match)}
                  >
                    ✓ Kies
                  </Button>
                </div>
              ))}
            </div>
          )}

          <div className="match-actions">
            <Button variant="secondary" onClick={() => setMatchingTrack(null)}>
              ← Terug
            </Button>
            {matchingTrack.previewUrl && (
              <Button 
                variant="warning" 
                onClick={() => handleAddWithPreview(matchingTrack)}
              >
                🎧 Gebruik 30s Preview
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="spotify-search">
      {/* Spotify Web Playback SDK Player */}
      <SpotifyPlayer />

      <div className="search-header">
        <h3>🎧 Spotify Zoeken</h3>
        <div className="search-header-actions">
          <button 
            className={`toggle-advanced ${showAdvanced ? 'active' : ''}`}
            onClick={() => setShowAdvanced(!showAdvanced)}
          >
            {showAdvanced ? '− Simpel zoeken' : '+ Geavanceerd zoeken'}
          </button>
        </div>
      </div>

      {/* Decade quick filters */}
      <div className="decade-filters">
        {DECADES.map(decade => (
          <button
            key={decade.label}
            className={`decade-btn ${selectedDecade === decade.label ? 'active' : ''}`}
            onClick={() => handleDecadeClick(decade)}
          >
            {decade.label}
          </button>
        ))}
      </div>

      {/* Simple search bar */}
      {!showAdvanced && (
        <div className="search-bar">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="Zoek artiest, nummer of album..."
          />
          <Button variant="primary" onClick={handleSearch} disabled={loading}>
            {loading ? '...' : '🔍'}
          </Button>
        </div>
      )}

      {/* Advanced search filters */}
      {showAdvanced && (
        <div className="advanced-search">
          <div className="filter-row">
            <div className="filter-field">
              <label>Artiest</label>
              <input
                type="text"
                value={artistFilter}
                onChange={(e) => setArtistFilter(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="bijv. Queen"
              />
            </div>
            <div className="filter-field">
              <label>Nummer</label>
              <input
                type="text"
                value={trackFilter}
                onChange={(e) => setTrackFilter(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="bijv. Bohemian Rhapsody"
              />
            </div>
          </div>
          <div className="filter-row">
            <div className="filter-field">
              <label>Album</label>
              <input
                type="text"
                value={albumFilter}
                onChange={(e) => setAlbumFilter(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="bijv. A Night at the Opera"
              />
            </div>
            <div className="filter-field filter-year-range">
              <label>Jaar</label>
              <div className="year-inputs">
                <input
                  type="number"
                  value={yearFrom}
                  onChange={(e) => setYearFrom(e.target.value)}
                  placeholder="Van"
                  min="1900"
                  max="2030"
                />
                <span>-</span>
                <input
                  type="number"
                  value={yearTo}
                  onChange={(e) => setYearTo(e.target.value)}
                  placeholder="Tot"
                  min="1900"
                  max="2030"
                />
              </div>
            </div>
          </div>
          <div className="filter-row">
            <div className="filter-field">
              <label>Extra zoektermen</label>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Overige zoektermen..."
              />
            </div>
          </div>
          <div className="filter-options">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={onlyWithPreview}
                onChange={(e) => setOnlyWithPreview(e.target.checked)}
              />
              Alleen nummers met 30s preview
            </label>
            <div className="limit-selector">
              <label>Aantal resultaten:</label>
              <select value={searchLimit} onChange={(e) => setSearchLimit(Number(e.target.value))}>
                <option value={5}>5</option>
                <option value={10}>10</option>
              </select>
            </div>
          </div>
          <div className="filter-actions">
            <Button variant="secondary" onClick={clearFilters}>
              ✕ Wissen
            </Button>
            <Button variant="primary" onClick={handleSearch} disabled={loading}>
              {loading ? 'Zoeken...' : '🔍 Zoeken'}
            </Button>
          </div>
        </div>
      )}

      {error && <p className="error-message">{error}</p>}

      {/* Batch selection actions */}
      {results.length > 0 && (
        <div className="batch-actions">
          <span className="results-count">{results.length} resultaten</span>
          <button className="select-all-btn" onClick={selectAllWithPreview}>
            Selecteer alle met preview ({results.filter(t => t.previewUrl && !addedTrackIds.has(t.id)).length})
          </button>
          {selectedTracks.size > 0 && (
            <Button variant="success" size="small" onClick={handleAddSelectedTracks}>
              ➕ Voeg {selectedTracks.size} toe
            </Button>
          )}
        </div>
      )}

      <div className="search-results">
        {results.map((track) => (
          <div 
            key={track.id} 
            className={`track-result ${selectedTracks.has(track.id) ? 'selected' : ''} ${addedTrackIds.has(track.id) ? 'added' : ''}`}
          >
            {/* Selection checkbox */}
            {!addedTrackIds.has(track.id) && (
              <div className="track-select">
                <input
                  type="checkbox"
                  checked={selectedTracks.has(track.id)}
                  onChange={() => toggleTrackSelection(track.id)}
                />
              </div>
            )}
            <div className="track-thumb">
              {track.thumbnail ? (
                <img src={track.thumbnail} alt={track.title} />
              ) : (
                <div className="no-thumb">🎵</div>
              )}
            </div>
            <div className="track-info">
              <div className="track-title">{track.title}</div>
              <div className="track-artist">{track.artist}</div>
              <div className="track-album">{track.album}</div>
              <div className="track-meta">
                {track.year && <span className="track-year">{track.year}</span>}
                <span className="track-duration">{formatDuration(track.duration)}</span>
                {track.previewUrl && <span className="has-preview">🎧 Preview</span>}
                {addedTrackIds.has(track.id) && <span className="track-added">✓ Toegevoegd</span>}
              </div>
            </div>
            <div className="track-actions">
              {/* Spotify Play Button (if authenticated with Premium) */}
              {isAuthenticated && isPremium && isReady && (
                <SpotifyPlayButton 
                  trackUri={`spotify:track:${track.id}`}
                  trackName={track.title}
                />
              )}
              {/* Add to playlist with Spotify URI (for Premium playback during game) */}
              {isAuthenticated && isPremium && !addedTrackIds.has(track.id) && (
                <Button
                  variant="success"
                  size="small"
                  onClick={() => handleAddWithSpotify(track)}
                >
                  ➕ Spotify
                </Button>
              )}
              {!addedTrackIds.has(track.id) && (
                <Button
                  variant="primary"
                  size="small"
                  onClick={() => handleFindYouTube(track)}
                >
                  🎬 YouTube
                </Button>
              )}
              {track.previewUrl && !addedTrackIds.has(track.id) && (
                <Button
                  variant="secondary"
                  size="small"
                  onClick={() => handleAddWithPreview(track)}
                >
                  30s
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="spotify-footer">
        <button 
          className="reconfigure-btn"
          onClick={() => setShowConfig(true)}
        >
          ⚙️ Spotify Opnieuw Configureren
        </button>
      </div>
    </div>
  );
};

export default SpotifySearch;
